import React from 'react'
import type { Chapter } from '../../store/booksStore'
import { useBooksStore } from '../../store/booksStore'
import './ChapterCardCompact.css'

interface ChapterCardCompactProps {
    chapter: Chapter
    bookId: string
    onClick?: () => void
}

type ChapterStatus = 'complete' | 'processing' | 'pending'

const ChapterCardCompact: React.FC<ChapterCardCompactProps> = ({
    chapter,
    bookId,
    onClick
}) => {
    const { getChapterWitnesses } = useBooksStore()
    const witnesses = getChapterWitnesses(chapter.id)

    // Determine chapter status based on witness preprocessing states
    const getStatus = (): ChapterStatus => {
        if (witnesses.length === 0) return 'pending'

        const preprocessedCount = witnesses.filter(w =>
            w.preprocessing_result?.primary_chunking?.total_primary_chunks ||
            w.preprocessing_result?.chunks?.length
        ).length

        if (preprocessedCount === witnesses.length) return 'complete'
        if (preprocessedCount > 0) return 'processing'
        return 'pending'
    }

    const status = getStatus()
    const witnessCount = witnesses.length
    const preprocessedCount = witnesses.filter(w =>
        w.preprocessing_result?.primary_chunking?.total_primary_chunks ||
        w.preprocessing_result?.chunks?.length
    ).length
    const progress = witnessCount > 0 ? (preprocessedCount / witnessCount) * 100 : 0

    // Get most recent modification timestamp
    const getLastModified = (): number => {
        if (witnesses.length === 0) return chapter.created_at
        const witnessTimestamps = witnesses.map(w => w.last_modified || 0)
        return Math.max(chapter.created_at, ...witnessTimestamps)
    }

    const lastModified = getLastModified()

    // Format timestamp
    const getTimestampText = (): string => {
        const now = Date.now()
        const diff = now - lastModified

        if (diff < 60000) return 'just now'
        if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`
        if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`
        return `${Math.floor(diff / 86400000)}d ago`
    }

    const getStatusText = (): string => {
        if (status === 'complete') return 'All preprocessed'
        if (status === 'processing') return `${preprocessedCount}/${witnessCount} ready`
        return 'No witnesses'
    }

    const handleClick = () => {
        if (onClick) {
            onClick()
        } else {
            // Default navigation to chapter view
            window.location.hash = `#/books/${bookId}/chapters/${chapter.id}`
        }
    }

    return (
        <div
            className={`chapter-card-compact status-${status}`}
            onClick={handleClick}
        >
            <div className="card-stripe" />

            <div className="card-content">
                <div className="card-header-row">
                    <div className="chapter-name">
                        {chapter.name}
                    </div>
                    <svg
                        className="action-icon"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                        <circle cx="12" cy="12" r="1" />
                        <circle cx="12" cy="5" r="1" />
                        <circle cx="12" cy="19" r="1" />
                    </svg>
                </div>
                {chapter.description && (
                    <div className="chapter-description">{chapter.description}</div>
                )}
            </div>

            <div className="card-progress-track">
                <div
                    className="card-progress-fill"
                    style={{ width: `${progress}%` }}
                />
            </div>

            <div className="card-footer">
                <span>{getStatusText()}</span>
                <span className="meta-counts">
                    {witnessCount} {witnessCount === 1 ? 'witness' : 'witnesses'} • {getTimestampText()}
                </span>
            </div>
        </div>
    )
}

export default ChapterCardCompact
